<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>

    <template #content>
      <article class="blog-content">
        <section class="intro">
          <p>
            Creating a 360 virtual tour has never been easier. With today's
            AI-powered smartphone technology, you don't need expensive 360
            cameras or professional photography equipment. Travvir's free
            trial includes tour creation, hosting with a shareable link,
            iframe embed, and e-visits — plus pano-to-image and pano-to-video,
            which are always free. Download the Travvir app to get started.
            This comprehensive
            guide will walk you through the entire process of creating stunning
            360 virtual tours using just your smartphone.
          </p>
        </section>

        <section class="key-takeaways">
          <h2>Key Takeaways</h2>
          <ul>
            <li>
              Any smartphone with a 12MP+ camera can create professional 360°
              virtual tours.
            </li>
            <li>
              AI-powered apps handle stitching, editing, and hosting — no
              technical skills needed.
            </li>
            <li>
              A typical 5-room space takes 15-30 minutes to capture from start
              to finish.
            </li>
            <li>
              Shoot in landscape mode, maintain consistent height, and overlap
              frames for best results.
            </li>
            <li>
              Published tours include embed codes for websites, sharing links,
              and Google integration.
            </li>
          </ul>
        </section>

        <section>
          <h2>Why Create 360 Virtual Tours with Your Smartphone?</h2>
          <p>
            Smartphone-based 360 virtual tours have revolutionized how
            businesses showcase their spaces. Here's why this approach is
            gaining massive popularity:
          </p>
          <p>
            First and foremost, there's
            <strong>no expensive equipment needed</strong> – you can use the
            device you already own. The technology leverages
            <strong>AI-powered stitching</strong> where advanced algorithms
            create seamless panoramas automatically. You'll see
            <strong>instant results</strong>, with your virtual tour ready
            within minutes of capture. Another major advantage is
            <strong>easy updates</strong> – you can refresh your tour anytime
            without hiring photographers or scheduling complex shoots. Despite
            using just a smartphone, the
            <strong>professional quality</strong> is impressive, as modern
            smartphones produce 4K+ resolution images that rival dedicated
            camera equipment.
          </p>
        </section>
        <div
          style="
            position: relative;
            height: 400px;
            margin: 2rem 0;
            border-radius: 14px;
            overflow: hidden;
          "
        >
          <MarzipanoPreview
            :imageUrl="`${cdnUrl}/61fcc50f0428aba3fd5dce60/4b2dd0db-30ab-486b-bf75-9ab8f6b40c36`"
          />
        </div>
        <section>
          <h2>What You'll Need</h2>
          <p>
            Before we dive into the step-by-step process, make sure you have:
          </p>
          <p>
            You'll need a smartphone (iPhone or Android) with a good camera, and
            the Travvir app from the App Store or Google Play. Travvir offers a
            free trial — download the Travvir app to start your free trial.
            Ensure you have good lighting in your space, and set aside about 2
            minutes per room for the capture process. Optionally, a smartphone
            tripod can provide additional stability during shooting.
          </p>
        </section>

        <section>
          <h2>Step-by-Step Guide to Creating Your 360 Virtual Tour</h2>

          <h3>Step 1: Download and Set Up the Travvir App</h3>
          <p>
            Start by downloading the Travvir app from your device's app store.
            Travvir offers a free trial, giving you plenty of opportunity to
            create your first virtual tours. Download the Travvir app to start
            your free trial.
          </p>
          <p>
            Begin by downloading Travvir from the App Store or Google Play to
            start your free trial. Sign in with email or social login, then
            complete the brief onboarding tutorial to familiarize yourself with
            the interface. Finally, grant the necessary camera and location
            permissions so the app can function properly.
          </p>

          <h3>Step 2: Prepare Your Space</h3>
          <p>
            Proper preparation makes a significant difference in your final
            result:
          </p>
          <p>
            Start by cleaning and decluttering the area you want to capture,
            removing any unnecessary items that might distract from the space.
            Turn on all lights for optimal brightness, ensuring every corner is
            well-lit. Open curtains to let in natural light, which creates a
            more inviting atmosphere. Make sure to hide personal items or
            sensitive information that shouldn't be visible in the tour.
            Finally, arrange furniture to highlight the space's best features,
            creating the most appealing visual flow.
          </p>

          <h3>Step 3: Position Yourself Correctly</h3>
          <p>Your position when capturing matters for the best results:</p>
          <p>
            Stand in the center of the room for balanced coverage, which ensures
            all walls and features are equally represented. Hold your phone at
            chest height, approximately the natural eye level for most viewers.
            Avoid standing near mirrors or reflective surfaces where you might
            appear in the final panorama. Make sure you have 360-degree
            clearance around you, with no obstructions that could interfere with
            the capture process.
          </p>

          <h3>Step 4: Capture Your 360 Photos</h3>
          <p>
            The Travvir app guides you through the capture process with
            real-time visual cues:
          </p>
          <p>
            Open the Travvir app and tap "Create New Place" to begin your
            project. Name your project to keep your tours organized. Tap "Add
            360" to start the photo capture process. Follow the on-screen guides
            to capture photos in all directions, rotating smoothly as prompted.
            The AI will automatically detect when you've captured enough
            coverage of the space. Once complete then start the upload process.
          </p>

          <h3>Step 5: Let AI Work Its Magic</h3>
          <p>Once you've captured all angles, Travvir's AI takes over:</p>
          <p>
            The AI stitches your photos into a seamless 360-degree panorama,
            blending each image perfectly. It automatically corrects lighting
            and exposure differences between shots, ensuring consistent
            brightness throughout. The system removes visible artifacts and
            stitching lines that might otherwise be noticeable. It enhances
            image quality and sharpness using advanced algorithms. The entire
            processing typically takes some time per scene, but the result is a
            professional-quality virtual tour that looks like it was created
            with high-end equipment.
          </p>

          <h3>Step 6: Add Multiple Rooms and Create Navigation</h3>
          <p>A complete virtual tour includes multiple connected scenes:</p>
          <p>
            Repeat the capture process for each room or area you want to include
            in your tour. Use the app to add navigation hotspots between scenes,
            creating clickable links that allow viewers to move from one space
            to another. Create a logical flow through your space that mirrors
            how someone would naturally walk through it. Add labels and
            descriptions to help viewers navigate and understand what they're
            seeing in each scene.
          </p>

          <h3>Step 7: Enhance with Information and Branding</h3>
          <p>Make your virtual tour more informative and professional:</p>
          <p>
            Add text descriptions to each scene that provide context and
            highlight key features. Include your logo and branding elements to
            maintain consistent professional identity throughout the tour. Add
            information hotspots to highlight special features or details that
            viewers should notice. Include contact information and
            call-to-action buttons that make it easy for interested viewers to
            reach out or take next steps. You can even embed videos or images
            for additional context that enhances the immersive experience.
          </p>

          <h3>Step 8: Publish and Share</h3>
          <p>Once you're satisfied with your virtual tour:</p>
          <p>
            Get a shareable link for your tour that you can distribute anywhere.
            Embed the tour on your website using the provided code, making it a
            central part of your online presence. Share on social media
            platforms to maximize reach and engagement. Track views and
            engagement through the analytics dashboard, giving you valuable
            insights into how people interact with your tour.
          </p>
        </section>

        <section>
          <h2>Pro Tips for Better Results</h2>
          <p>
            <strong>Shoot during golden hour</strong> – early morning or late
            afternoon light creates warmth and adds a professional quality to
            your captures. <strong>Use a tripod</strong> even if it's just a
            small smartphone tripod, as it dramatically improves stability and
            reduces blur. <strong>Capture at eye level</strong> to create the
            most natural viewing experience that feels authentic to visitors.
            <strong>Avoid movement</strong> by keeping as still as possible
            during capture to prevent distortion and blurring.
            <strong>Shoot in landscape mode</strong> which provides a wider
            field of view and better coverage of the space. Finally,
            <strong>test different times</strong> throughout the day, as
            lighting changes can dramatically affect the mood and quality of
            your virtual tour.
          </p>
        </section>

        <section>
          <h2>Common Mistakes to Avoid</h2>
          <p>
            One of the most common errors is rushing the capture process – take
            your time for best results as patience pays off in quality. Avoid
            shooting in poor lighting, remembering that natural light is your
            friend and essential for appealing images. Don't stand too close to
            walls; center positioning is key to capturing the full space
            effectively. Many people forget to check their surroundings and
            remove unwanted items before shooting, so always do a final sweep.
            Finally, never skip testing the tour before sharing – always preview
            first to catch any issues before your audience sees them.
          </p>
        </section>

        <section>
          <h2>Use Cases for Smartphone 360 Virtual Tours</h2>
          <p>This technology is perfect for:</p>
          <p>
            <strong>Real Estate</strong> professionals can show properties to
            remote buyers, eliminating geographical barriers.
            <strong>Hotels & Restaurants</strong> can let guests explore
            facilities before booking, building confidence and trust.
            <strong>Retail Stores</strong> can create virtual shopping
            experiences that extend their reach beyond physical locations.
            <strong>Event Venues</strong> can help clients visualize their event
            in the actual space, improving booking rates.
            <strong>Museums & Galleries</strong> can provide virtual exhibitions
            that make art and culture accessible to everyone.
            <strong>Educational Institutions</strong> can offer campus tours to
            prospective students anywhere in the world.
          </p>
        </section>

        <section>
          <h2>Conclusion</h2>
          <p>
            Creating professional 360 virtual tours with your smartphone is not
            only possible but incredibly practical and cost-effective. With
            AI-powered apps like Travvir, you can produce high-quality virtual
            tours that rival those created with expensive professional
            equipment.
          </p>
          <p>
            The best part? You can start right now with the device in your
            pocket. Download the Travvir app, follow this guide, and create your
            first 360 virtual tour today. The free trial covers tour creation,
            hosting, embed, and e-visits — and pano-to-image and pano-to-video
            are always free. With practice, you'll be creating
            stunning immersive experiences that engage your audience and
            showcase your spaces like never before.
          </p>
        </section>
      </article>
    </template>
    <section>
      <h2>Continue Reading</h2>
      <ul>
        <li><a href="/blog/en/free-virtual-tour-creator">Complete 360 Tour Guide</a></li>
        <li>
          <a href="/blog/en/360-virtual-tour-from-phone-beginners"
            >Beginner's Guide to 360 Tours</a
          >
        </li>
        <li>
          <a href="/blog/en/how-to-make-virtual-tour-iphone"
            >iPhone Virtual Tour Guide</a
          >
        </li>
        <li>
          <a href="/blog/en/create-virtual-tour-with-phone"
            >Create Tours with Your Phone</a
          >
        </li>
        <li>
          <a href="/blog/en/best-smartphone-virtual-tour-app"
            >Best Smartphone Tour Apps</a
          >
        </li>
        <li>
          <a href="/blog/en/360-virtual-tour-mistakes"
            >Avoid Common Virtual Tour Mistakes</a
          >
        </li>
        <li>
          <a href="/blog/en/free-virtual-tour-creator"
            >Create a Tour for Free</a
          >
        </li>
      </ul>
    </section>

    <RelatedPosts
      :currentSlug="postData.slug"
      :currentCategories="postData.categories"
    />
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const config = useRuntimeConfig();
const cdnUrl = config.public.cdnUrl;

// Find post data from JSON by slug
const postData = blogPostsData.find(
  (post) => post.slug === "360-virtual-tour-smartphone-guide",
);

// SEO Meta Tags
// Structured Data - Article Schema
useBlogSeo({
  title: "How to Make a Virtual Tour with Your Phone [2026] | Travvir",
  description:
    "How do I make a virtual tour with my phone? Step-by-step guide — apps, shooting, AI stitching, and publishing a 360 tour using only your smartphone.",
  keywords:
    "how to make a virtual tour with phone, how do i make a virtual tour with my phone, smartphone virtual tour, 360 photo app, create 360 tour phone, how to make virtual tour",
  slug: "360-virtual-tour-smartphone-guide",
  image: postData?.image,
  postData,
  lang: "en",
});

</script>

<style lang="scss" scoped>
// Component-specific styles (if needed)
</style>
